import BookingOutreachDialog from "@/components/booking/BookingOutreachDialog";
import { useArtistScopeSafe } from "@/lib/artistContext";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { LockedSection, UnlockedSection } from "@/components/locked/LockedSections";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LeadsKanbanBoard,
  KANBAN_COLUMNS,
  deriveLeadStage,
  type KanbanStageId,
} from "@/components/booking/LeadsKanbanBoard";
import { useBookingLeads } from "@/hooks/booking/useBookingLeads";
import {
  bookingErrorMessage,
  bookingHasShow,
  eventFormDefaults,
  getBookingLeadContact,
  leadDetailPath,
  mapBookingLeadToCrmLead,
} from "@/lib/booking";
import { fetchContact, fetchContacts, fetchLeadEmailSummary, type LeadCreatePayload, type LeadEmailSummary } from "@/api/crm";
import { createManualBookingEvent, createManualBookingLead, denyBookingLead, updateBookingLeadStage, updateBookingLeadWindow } from "@/api/booking";
import { useToast } from "@/components/ToastProvider";
import { useSessionUser } from "@/hooks/useSessionUser";
import { EmailDrawer } from "@/components/email/EmailDrawer";
import { LeadDetailDrawer, type LeadDetailFormState } from "@/components/booking/LeadDetailDrawer";
import { BookingEventDrawer } from "@/components/booking/BookingEventDrawer";
import type { BookingEventSubmission } from "@/components/booking/BookingEventForm";
import type { CrmContact, CrmLead } from "@/types/crm";

const ALL_STAGES_SELECT_VALUE = "all-stages" as const;
const ANY_STATE_SELECT_VALUE = "any-state";

const US_STATE_OPTIONS = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

export default function BookingPage() {
  const { selectedArtistId } = useArtistScopeSafe();
  const [outreachSelection, setOutreachSelection] = useState<CrmLead[] | null>(null);
  useEffect(() => { setOutreachSelection(null); setSelectedLeadIds([]); }, [selectedArtistId]);
  const { leads, loading, error, summary, refresh } = useBookingLeads({ status: "pending,confirmed", limit: 100 });
  const [stageOverrides, setStageOverrides] = useState<Record<number, KanbanStageId>>({});
  const crmLeads = useMemo(
    () =>
      leads.map((record) => {
        const lead = mapBookingLeadToCrmLead(record);
        const override = stageOverrides[lead.id];
        if (!override) return lead;
        return { ...lead, pipeline_stage: override };
      }),
    [leads, stageOverrides],
  );
  const { push } = useToast();
  const { user } = useSessionUser();
  // Legacy CRM tools (manual lead search, Booking Agent) stay available to owners only.
  const showLegacyTools = Boolean(user?.is_owner);
  const navigate = useNavigate();
  const location = useLocation();

  const [viewMode, setViewMode] = useState<"table" | "kanban">("kanban");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState(ANY_STATE_SELECT_VALUE);
  const [searching, setSearching] = useState(false);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [contactEventWindows, setContactEventWindows] = useState<
    Record<number, { startWindow: string; endWindow: string }>
  >({});
  const [contactPage, setContactPage] = useState(1);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkEmailing, setBulkEmailing] = useState(false);
  const [creatingId, setCreatingId] = useState<number | null>(null);
  const [eventQueueIds, setEventQueueIds] = useState<number[]>([]);
  const [eventQueueIndex, setEventQueueIndex] = useState(0);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [bulkWindowStart, setBulkWindowStart] = useState("");
  const [bulkWindowEnd, setBulkWindowEnd] = useState("");
  const [emailSummaries, setEmailSummaries] = useState<Record<number, LeadEmailSummary>>({});
  const leadIdsKeyRef = useRef("");
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [emailLead, setEmailLead] = useState<CrmLead | null>(null);
  const [emailContact, setEmailContact] = useState<CrmContact | null>(null);
  const [emailLeadIds, setEmailLeadIds] = useState<number[]>([]);
  const [emailLeadIndex, setEmailLeadIndex] = useState(0);
  const [detailLead, setDetailLead] = useState<CrmLead | null>(null);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!Object.keys(stageOverrides).length) return;
    setStageOverrides((prev) => {
      let next = prev;
      leads.forEach((record) => {
        const lead = mapBookingLeadToCrmLead(record);
        const override = prev[lead.id];
        if (override && deriveLeadStage(lead) === override) {
          if (next === prev) next = { ...prev };
          delete next[lead.id];
        }
      });
      return next;
    });
  }, [leads, stageOverrides]);

  const refreshEmailSummaries = useCallback(
    async (leadIds: number[]) => {
      if (!leadIds.length) {
        setEmailSummaries({});
        return;
      }
      try {
        const payload = await fetchLeadEmailSummary(leadIds);
        const map: Record<number, LeadEmailSummary> = {};
        (payload.items ?? []).forEach((item) => {
          map[item.lead_id] = item;
        });
        setEmailSummaries(map);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to load email activity.";
        push({ title: "Email activity", message });
      }
    },
    [push],
  );

  useEffect(() => {
    const ids = crmLeads.map((lead) => lead.id);
    const key = ids.join(",");
    if (key === leadIdsKeyRef.current) {
      return;
    }
    leadIdsKeyRef.current = key;
    // Booking lead ids are booking_lead_recommendations ids, not crm_leads ids, so
    // the CRM email-summary endpoint would return unrelated data. Owners only.
    if (showLegacyTools) void refreshEmailSummaries(ids);
  }, [crmLeads, refreshEmailSummaries, showLegacyTools]);
  const highlightedLeadId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("leadId");
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }, [location.search]);

  const stageOptions: KanbanStageId[] = useMemo(() => KANBAN_COLUMNS.map((col) => col.id), []);
  const [stageFilter, setStageFilter] = useState<KanbanStageId | "">("");
  const stageSelectValue = stageFilter || ALL_STAGES_SELECT_VALUE;
  const handleStageFilterChange = useCallback((value: string) => {
    setStageFilter(value === ALL_STAGES_SELECT_VALUE ? "" : (value as KanbanStageId));
  }, []);
  const CONTACTS_PER_PAGE = 20;
  const contactSearchLimit = 500;
  const totalContactPages = Math.max(1, Math.ceil(contacts.length / CONTACTS_PER_PAGE));
  const currentPageContacts = useMemo(() => {
    const start = (contactPage - 1) * CONTACTS_PER_PAGE;
    return contacts.slice(start, start + CONTACTS_PER_PAGE);
  }, [contacts, contactPage]);
  const selectedContacts = useMemo(
    () => contacts.filter((contact) => selectedContactIds.includes(contact.id)),
    [contacts, selectedContactIds],
  );
  const allSelectedOnPage =
    currentPageContacts.length > 0 && currentPageContacts.every((contact) => selectedContactIds.includes(contact.id));

  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const allLeadIds = useMemo(() => crmLeads.filter(lead => !stageFilter || deriveLeadStage(lead) === stageFilter).map(lead => lead.id), [crmLeads, stageFilter]);
  const allLeadsSelected = allLeadIds.length > 0 && allLeadIds.every(id => selectedLeadIds.includes(id));
  const handleToggleLeadSelection = useCallback(
    (leadId: number) => {
      setSelectedLeadIds((prev) => {
        if (prev.includes(leadId)) return prev.filter((id) => id !== leadId);
        return [...prev, leadId];
      });
    },
    [],
  );
  const toggleSelectAllLeads = useCallback(() => {
    if (allLeadsSelected) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(allLeadIds);
    }
  }, [allLeadIds, allLeadsSelected]);
  const leadsRef = useRef(leads);
  const crmLeadsRef = useRef(crmLeads);

  useEffect(() => {
    leadsRef.current = leads;
  }, [leads]);

  useEffect(() => {
    crmLeadsRef.current = crmLeads;
  }, [crmLeads]);

  const loadEmailContext = useCallback(
    async (leadId: number) => {
      const record = leadsRef.current.find((item) => item.id === leadId);
      const lead = crmLeadsRef.current.find((item) => item.id === leadId);
      if (!record || !lead) return;
      setEmailLead(lead);
      setEmailContact(null);
      const contactInfo = getBookingLeadContact(record);
      const fallback: CrmContact = {
        id: contactInfo.id ?? -record.id,
        name: contactInfo.name ?? lead.title ?? "Booking lead",
        contact_name: contactInfo.name ?? lead.title ?? undefined,
        company_name: contactInfo.company_name,
        contact_type: "Venue",
        role_id: "BOOKER",
        email: contactInfo.email,
        phone: contactInfo.phone,
        location_city: contactInfo.city ?? lead.region ?? undefined,
        location_region: contactInfo.region ?? contactInfo.state ?? undefined,
        country: contactInfo.country,
        tags: contactInfo.tags ?? [],
        source_urls: contactInfo.source_urls ?? [],
        social_links: contactInfo.social_links ?? [],
        owner: undefined,
        crm_stage: undefined,
        source_url: undefined,
        last_booking_score: undefined,
        notes: undefined,
        created_at: lead.created_at,
        updated_at: lead.updated_at,
      };
      setEmailContact(fallback);
      if (contactInfo.id) {
        try {
          const contact = await fetchContact(contactInfo.id);
          setEmailContact(contact);
          return;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Unable to load contact.";
          push({ title: "Contact lookup failed", message });
        }
      }
    },
    [push],
  );

  const openEmailSequence = useCallback(
    (leadIds: number[], startIndex = 0) => {
      if (!leadIds.length) return;
      const clampedIndex = Math.max(0, Math.min(startIndex, leadIds.length - 1));
      setEmailLeadIds(leadIds);
      setEmailLeadIndex(clampedIndex);
      setEmailDrawerOpen(true);
      void loadEmailContext(leadIds[clampedIndex]);
    },
    [loadEmailContext],
  );

  const handleBulkSendEmail = useCallback(() => {
    if (!selectedLeadIds.length) return;
    openEmailSequence(selectedLeadIds, 0);
  }, [openEmailSequence, selectedLeadIds]);

  const handleSequencePrev = useCallback(() => {
    if (emailLeadIndex <= 0) return;
    const nextIndex = emailLeadIndex - 1;
    setEmailLeadIndex(nextIndex);
    void loadEmailContext(emailLeadIds[nextIndex]);
  }, [emailLeadIds, emailLeadIndex, loadEmailContext]);

  const handleSequenceNext = useCallback(() => {
    if (emailLeadIndex >= emailLeadIds.length - 1) return;
    const nextIndex = emailLeadIndex + 1;
    setEmailLeadIndex(nextIndex);
    void loadEmailContext(emailLeadIds[nextIndex]);
  }, [emailLeadIds, emailLeadIndex, loadEmailContext]);
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const handleBulkDismiss = useCallback(async () => {
    if (!selectedLeadIds.length) return;
    setBulkDismissing(true);
    let success = 0;
    for (const id of selectedLeadIds) {
      try {
        await denyBookingLead(id);
        success += 1;
      } catch {
        // ignore errors for now
      }
    }
    setBulkDismissing(false);
    setSelectedLeadIds([]);
    if (success) {
      push({ title: "Leads dismissed", message: `${success} leads dismissed.` });
      await refresh();
    } else {
      push({ title: "Dismiss failed", message: "Unable to dismiss selected leads." });
    }
  }, [selectedLeadIds, push, refresh]);
  const filteredLeads = useMemo(() => {
    if (!stageFilter) return crmLeads;
    return crmLeads.filter((lead) => deriveLeadStage(lead) === stageFilter);
  }, [crmLeads, stageFilter]);

  const handleSearchContacts = useCallback(async () => {
    setSearching(true);
    try {
      const regionFilter = stateFilter === ANY_STATE_SELECT_VALUE ? undefined : stateFilter;
      const result = await fetchContacts({
        query: searchTerm,
        region: regionFilter,
        limit: contactSearchLimit,
      });
      setContacts(result.contacts ?? []);
      setContactPage(1);
      setSelectedContactIds([]);
      setContactEventWindows({});
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to search contacts.";
      push({ title: "Search failed", message });
    } finally {
      setSearching(false);
    }
  }, [contactSearchLimit, push, searchTerm, stateFilter]);

  const updateContactEventWindow = useCallback(
    (contactId: number, field: "startWindow" | "endWindow", value: string) => {
      setContactEventWindows((prev) => {
        const existing = prev[contactId] ?? { startWindow: "", endWindow: "" };
        return { ...prev, [contactId]: { ...existing, [field]: value } };
      });
    },
    [],
  );

  const handleCreateLead = useCallback(
    async (contact: CrmContact) => {
      if (!contact?.id) return;
      setCreatingId(contact.id);
      try {
        const windowDetails = contactEventWindows[contact.id];
        const eventWindow = deriveEventWindow(windowDetails);
        await createManualBookingLead({
          contact_id: contact.id,
          contact_name: contact.contact_name ?? contact.name,
          company_name: contact.company_name ?? contact.name,
          city: contact.location_city ?? undefined,
          state: contact.location_region ?? undefined,
          region: contact.location_region ?? undefined,
          country: contact.country ?? undefined,
          tags: contact.tags ?? undefined,
          pipeline_stage: "prospecting",
          source: "CRM",
          window_start: eventWindow?.start ?? undefined,
          window_end: eventWindow?.end ?? undefined,
        });
        push({ title: "Lead created", message: `${contact.name ?? contact.company_name ?? "Contact"} added to booking.` });
        void refresh();
        } catch (err) {
          const message = err instanceof Error ? err.message : "Unable to create lead.";
          push({ title: "Lead creation failed", message });
        } finally {
          setCreatingId(null);
        }
      },
      [contactEventWindows, push, refresh],
    );

  const handleBulkCreate = useCallback(async () => {
    if (!selectedContactIds.length) return;
    setBulkCreating(true);
    let successCount = 0;
    for (const contactId of selectedContactIds) {
      const contact = contacts.find((item) => item.id === contactId);
      if (!contact?.id) continue;
      try {
        const windowDetails = contactEventWindows[contact.id];
        const eventWindow = deriveEventWindow(windowDetails);
        await createManualBookingLead({
          contact_id: contact.id,
          contact_name: contact.contact_name ?? contact.name,
          company_name: contact.company_name ?? contact.name,
          city: contact.location_city ?? undefined,
          state: contact.location_region ?? undefined,
          region: contact.location_region ?? undefined,
          country: contact.country ?? undefined,
          tags: contact.tags ?? undefined,
          pipeline_stage: "prospecting",
          source: "CRM",
          window_start: eventWindow?.start ?? undefined,
          window_end: eventWindow?.end ?? undefined,
        });
        successCount += 1;
      } catch {
        // swallow per-contact errors
      }
    }
    setSelectedContactIds([]);
    setBulkCreating(false);
    if (successCount) {
      push({ title: "Bulk leads created", message: `${successCount} contacts added to booking.` });
      void refresh();
    } else {
      push({ title: "Bulk creation", message: "No contacts were added. Try again later." });
    }
  }, [contacts, contactEventWindows, push, refresh, selectedContactIds]);

  const handleBulkCreateAndEmail = useCallback(async () => {
    if (!selectedContactIds.length) return;
    setBulkEmailing(true);
    let successCount = 0;
    const createdLeadIds: number[] = [];
    for (const contactId of selectedContactIds) {
      const contact = contacts.find((item) => item.id === contactId);
      if (!contact?.id) continue;
      try {
        const windowDetails = contactEventWindows[contact.id];
        const eventWindow = deriveEventWindow(windowDetails);
        const response = await createManualBookingLead({
          contact_id: contact.id,
          contact_name: contact.contact_name ?? contact.name,
          company_name: contact.company_name ?? contact.name,
          city: contact.location_city ?? undefined,
          state: contact.location_region ?? undefined,
          region: contact.location_region ?? undefined,
          country: contact.country ?? undefined,
          tags: contact.tags ?? undefined,
          pipeline_stage: "prospecting",
          source: "CRM",
          window_start: eventWindow?.start ?? undefined,
          window_end: eventWindow?.end ?? undefined,
        });
        if (response?.id) createdLeadIds.push(response.id);
        successCount += 1;
      } catch {
        // swallow per-contact errors
      }
    }
    setSelectedContactIds([]);
    setBulkEmailing(false);
    if (successCount) {
      push({ title: "Leads created", message: `${successCount} contacts added to booking.` });
      await refresh();
      if (createdLeadIds.length) {
        openEmailSequence(createdLeadIds, 0);
        return;
      }
      push({ title: "Email", message: "Leads created, but email sequence could not be prepared yet. Try again." });
    } else {
      push({ title: "Bulk creation", message: "No contacts were added. Try again later." });
    }
  }, [contacts, contactEventWindows, openEmailSequence, push, refresh, selectedContactIds]);

  const handleBulkWindowApply = useCallback(
    (targetContacts: CrmContact[]) => {
      if (!targetContacts.length) return;
      const startValue = bulkWindowStart.trim();
      const endValue = bulkWindowEnd.trim();
      if (!startValue && !endValue) {
        push({ title: "Bulk window", message: "Set at least one date before applying." });
        return;
      }
      setContactEventWindows((prev) => {
        const next = { ...prev };
        targetContacts.forEach((contact) => {
          const current = prev[contact.id] ?? { startWindow: "", endWindow: "" };
          next[contact.id] = {
            startWindow: startValue || current.startWindow,
            endWindow: endValue || current.endWindow,
          };
        });
        return next;
      });
    },
    [bulkWindowStart, bulkWindowEnd, push],
  );

  const buildEventDatetime = useCallback((value?: string | null) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return `${trimmed}T20:00`;
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return "";
    const pad = (token: number) => token.toString().padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(
      parsed.getMinutes(),
    )}`;
  }, []);

  const currentEventQueueId = eventQueueIds[eventQueueIndex];
  const currentEventRecord = useMemo(
    () => leads.find((record) => record.id === currentEventQueueId) ?? null,
    [leads, currentEventQueueId],
  );
  const currentEventLead = useMemo(
    () => (currentEventRecord ? mapBookingLeadToCrmLead(currentEventRecord) : null),
    [currentEventRecord],
  );
  const currentEventInitialValues = useMemo(() => {
    if (!currentEventRecord || !currentEventLead) return undefined;
    const eventWindowStart = currentEventLead.event_window?.start ?? currentEventLead.event_window?.end ?? null;
    return {
      ...eventFormDefaults(currentEventRecord),
      event_datetime: buildEventDatetime(eventWindowStart) || undefined,
    };
  }, [buildEventDatetime, currentEventLead, currentEventRecord]);

  const enqueueEventForLead = useCallback((leadId: number) => {
    setEventQueueIds((prev) => (prev.includes(leadId) ? prev : [...prev, leadId]));
  }, []);

  const handleStageChange = useCallback(
    async (leadId: number, stage: KanbanStageId) => {
      const current = crmLeadsRef.current.find((item) => item.id === leadId);
      if (current && bookingHasShow(current) && stage !== "completed") {
        // The server enforces this too; stop here so the card never appears to move.
        push({
          title: "Booking already created a show",
          message: "This booking already created a show. Start a new booking opportunity to book this venue again.",
        });
        return;
      }
      setStageOverrides((prev) => ({ ...prev, [leadId]: stage }));
      try {
        await updateBookingLeadStage(leadId, stage);
        push({ title: "Stage updated", message: `Moved to ${stage}.` });
        if (stage === "completed") {
          enqueueEventForLead(leadId);
        }
        void refresh();
      } catch (err) {
        setStageOverrides((prev) => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
        push({ title: "Update failed", message: bookingErrorMessage(err, "Unable to update stage.") });
      }
    },
    [enqueueEventForLead, push, refresh],
  );

  const handleMoveLead = useCallback(
    (lead: { id: number }, nextStage: KanbanStageId) => {
      void handleStageChange(lead.id, nextStage);
    },
    [handleStageChange],
  );

  useEffect(() => {
    if (!eventQueueIds.length) {
      if (eventQueueIndex !== 0) setEventQueueIndex(0);
      return;
    }
    if (eventQueueIndex >= eventQueueIds.length) {
      setEventQueueIndex(eventQueueIds.length - 1);
    }
  }, [eventQueueIds, eventQueueIndex]);

  useEffect(() => {
    if (!currentEventQueueId) return;
    if (currentEventRecord) return;
    setEventQueueIds((prev) => prev.filter((id) => id !== currentEventQueueId));
  }, [currentEventQueueId, currentEventRecord]);

  const closeEventQueueItem = useCallback(() => {
    if (!eventQueueIds.length) return;
    setEventQueueIds((prev) => prev.filter((_, idx) => idx !== eventQueueIndex));
  }, [eventQueueIds.length, eventQueueIndex]);

  const handlePrevEvent = useCallback(() => {
    setEventQueueIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextEvent = useCallback(() => {
    setEventQueueIndex((prev) => Math.min(eventQueueIds.length - 1, prev + 1));
  }, [eventQueueIds.length]);

  const handleSubmitEvent = useCallback(
    async (values: BookingEventSubmission) => {
      if (!currentEventLead) return;
      setEventSubmitting(true);
      try {
        const response = await createManualBookingEvent(values);
        push(
          response.created === false
            ? { title: "Show already created", message: "This booking already has a show, so nothing was duplicated." }
            : { title: "Show created", message: "It's on your Shows page." },
        );
        closeEventQueueItem();
        void refresh();
      } catch (err) {
        // Re-throw a readable message: the form keeps its values and shows it in the drawer.
        throw new Error(bookingErrorMessage(err, "We couldn't create this show. Please try again."));
      } finally {
        setEventSubmitting(false);
      }
    },
    [closeEventQueueItem, currentEventLead, push, refresh],
  );

  const handleSaveWindow = useCallback(
    async (updates: LeadDetailFormState) => {
      if (!detailLead) return;
      await updateBookingLeadWindow(detailLead.id, updates.windowStart, updates.windowEnd);
      void refresh();
    },
    [detailLead, refresh],
  );

  const handleEditLead = useCallback(
    (lead: { id: number }) => {
      const target = crmLeads.find((item) => item.id === lead.id);
      if (target) setDetailLead(target);
    },
    [crmLeads],
  );

  const lockedItems: ReactNode[] = [];

  return (
    <div className="space-y-4">
      <UnlockedSection className="space-y-4">
        <header className="space-y-1">
          <h1 className="aa-page-title text-3xl font-semibold">Booking</h1>
          <p className="text-sm text-muted-foreground">
            Venues you're pursuing, from first pitch to booked show. Pitch venues from Discover, then mark them
            contacted once you've reached out.
          </p>
        </header>
        {!loading && !error && leads.length === 0 && (
          <div className="aa-glass-card rounded-2xl p-6 text-center" role="status" aria-label="No prospects yet">
            <p className="font-medium text-foreground">No venues in your pipeline yet.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Find a venue in Discover and choose Add to Booking or Pitch — it will show up here under Prospecting.
            </p>
            <Button asChild className="mt-4">
              <Link to="/discover">Go to Discover</Link>
            </Button>
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            We couldn't load your pipeline right now. Please refresh to try again.
          </p>
        )}
        {showLegacyTools && (
        <Accordion type="single" collapsible>
          <AccordionItem value="manual-leads" className="border-0">
            <Card className="aa-glass-card w-full">
              <CardHeader className="p-0">
                <AccordionTrigger className="px-4 py-3">
                  <div>
                    <CardTitle className="text-base font-semibold">Manual lead creation</CardTitle>
                    <p className="text-xs text-foreground/70">
                      Search contacts and create leads.
                    </p>
                  </div>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end">
                    <div className="flex-1 min-w-0">
                      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Search term
                        <Input
                          placeholder="Search contacts by name, city, or state"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          className="mt-1 w-full"
                        />
                      </label>
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        State filter (optional)
                        <Select value={stateFilter} onValueChange={(value) => setStateFilter(value)}>
                          <SelectTrigger className="mt-1 w-full rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-left text-sm text-foreground">
                            <SelectValue placeholder="Any state" />
                          </SelectTrigger>
                          <SelectContent className="border-border/60 bg-background/40">
                            <SelectItem value={ANY_STATE_SELECT_VALUE}>Any state</SelectItem>
                            {US_STATE_OPTIONS.map((option) => (
                              <SelectItem key={option.code} value={option.code}>
                                {option.code} — {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </label>
                    </div>
                    <Button onClick={() => void handleSearchContacts()} disabled={searching}>
                      {searching ? "Searching…" : "Search"}
                    </Button>
                  </div>
                  {contacts.length > 0 ? (
                    <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-background/40 p-4 md:flex-row md:items-end md:justify-between">
                      <div className="grid flex-1 gap-3 md:grid-cols-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Bulk start window
                          <input
                            type="date"
                            value={bulkWindowStart}
                            onChange={(event) => setBulkWindowStart(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-2 text-xs text-foreground"
                          />
                        </label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Bulk end window
                          <input
                            type="date"
                            value={bulkWindowEnd}
                            onChange={(event) => setBulkWindowEnd(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-2 text-xs text-foreground"
                          />
                        </label>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleBulkWindowApply(selectedContacts)}
                          disabled={!selectedContacts.length}
                        >
                          Apply to selected
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  {contacts.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">
                          {contacts.length} contact{contacts.length === 1 ? "" : "s"} found
                        </p>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <Button
                            size="sm"
                            onClick={handleBulkCreate}
                            disabled={!selectedContactIds.length || bulkCreating || bulkEmailing}
                          >
                            {bulkCreating ? "Sending…" : `Send to leads (${selectedContactIds.length})`}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleBulkCreateAndEmail}
                            disabled={!selectedContactIds.length || bulkCreating || bulkEmailing}
                          >
                            {bulkEmailing ? "Sending…" : `Send email (${selectedContactIds.length})`}
                          </Button>
                          <span className="text-muted-foreground">
                            {contactPage} / {totalContactPages}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setContactPage((prev) => Math.max(1, prev - 1))}
                            disabled={contactPage === 1}
                          >
                            Prev
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setContactPage((prev) => Math.min(totalContactPages, prev + 1))}
                            disabled={contactPage === totalContactPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-3 md:hidden">
                        <div className="flex items-center justify-between rounded-xl border border-border bg-background/30 px-3 py-2 text-xs text-muted-foreground">
                          <label className="inline-flex min-h-[44px] items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allSelectedOnPage}
                              onChange={(event) => {
                                const pageIds = currentPageContacts.map((contact) => contact.id);
                                setSelectedContactIds((prev) => {
                                  if (event.target.checked) {
                                    return Array.from(new Set([...prev, ...pageIds]));
                                  }
                                  return prev.filter((id) => !pageIds.includes(id));
                                });
                              }}
                              className="h-5 w-5 cursor-pointer rounded border border-border bg-background/70"
                            />
                            <span>Select all</span>
                          </label>
                          <span>
                            Page {contactPage} / {totalContactPages}
                          </span>
                        </div>
                        {currentPageContacts.map((contact) => (
                          <div key={contact.id} className="rounded-2xl border border-border bg-background/40 p-4 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={selectedContactIds.includes(contact.id)}
                                  onChange={(event) => {
                                    setSelectedContactIds((prev) => {
                                      if (event.target.checked) {
                                        return [...prev, contact.id];
                                      }
                                      return prev.filter((id) => id !== contact.id);
                                    });
                                  }}
                                  className="h-5 w-5 rounded border border-border bg-background/70"
                                />
                                <span className="sr-only">Select contact</span>
                              </label>
                              <div className="flex-1">
                                <p className="font-semibold text-foreground">
                                  {contact.name ?? contact.company_name ?? "Contact"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {contact.company_name ?? "No company"}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {contact.email ?? "No email"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {[contact.location_city, contact.location_region, contact.country]
                                    .filter(Boolean)
                                    .join(", ") || "Location unknown"}
                                </p>
                              </div>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2">
                              <label className="text-xs text-muted-foreground">
                                Start window
                                <input
                                  type="date"
                                  value={(contactEventWindows[contact.id]?.startWindow) ?? ""}
                                  onChange={(event) =>
                                    updateContactEventWindow(contact.id, "startWindow", event.target.value)
                                  }
                                  className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-2 text-xs text-foreground"
                                />
                              </label>
                              <label className="text-xs text-muted-foreground">
                                End window
                                <input
                                  type="date"
                                  value={(contactEventWindows[contact.id]?.endWindow) ?? ""}
                                  onChange={(event) =>
                                    updateContactEventWindow(contact.id, "endWindow", event.target.value)
                                  }
                                  className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-2 text-xs text-foreground"
                                />
                              </label>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => void handleCreateLead(contact)}
                              disabled={creatingId === contact.id}
                            >
                              {creatingId === contact.id ? "Creating…" : "Create lead"}
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="hidden md:block overflow-x-auto rounded-xl border border-border bg-background/30">
                        <table className="w-full text-sm text-left text-foreground/80">
                          <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                            <tr>
                              <th className="px-3 py-2">
                                <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                                  <input
                                    type="checkbox"
                                    checked={allSelectedOnPage}
                                    onChange={(event) => {
                                      const pageIds = currentPageContacts.map((contact) => contact.id);
                                      setSelectedContactIds((prev) => {
                                        if (event.target.checked) {
                                          return Array.from(new Set([...prev, ...pageIds]));
                                        }
                                        return prev.filter((id) => !pageIds.includes(id));
                                      });
                                    }}
                                    className="h-5 w-5 cursor-pointer rounded border border-border bg-background/70 md:h-4 md:w-4"
                                  />
                                  <span className="sr-only">Select all contacts on page</span>
                                </label>
                              </th>
                              <th className="px-3 py-2">Contact</th>
                              <th className="px-3 py-2">Email</th>
                              <th className="px-3 py-2">Location</th>
                              <th className="px-3 py-2">Start window</th>
                              <th className="px-3 py-2">End window</th>
                              <th className="px-3 py-2">Lead action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentPageContacts.map((contact) => (
                              <tr key={contact.id} className="border-t border-border">
                                <td className="px-3 py-2">
                                  <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                                    <input
                                      type="checkbox"
                                      checked={selectedContactIds.includes(contact.id)}
                                      onChange={(event) => {
                                        setSelectedContactIds((prev) => {
                                          if (event.target.checked) {
                                            return [...prev, contact.id];
                                          }
                                          return prev.filter((id) => id !== contact.id);
                                        });
                                      }}
                                      className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                                    />
                                    <span className="sr-only">Select contact</span>
                                  </label>
                                </td>
                                <td className="px-3 py-2">
                                  <p className="font-semibold text-foreground">
                                    {contact.name ?? contact.company_name ?? "Contact"}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {contact.company_name ?? "No company"}
                                  </p>
                                </td>
                                <td className="px-3 py-2">
                                  <p className="text-xs text-muted-foreground">{contact.email ?? "No email"}</p>
                                </td>
                                <td className="px-3 py-2">
                                  <p className="text-xs text-muted-foreground">
                                    {[contact.location_city, contact.location_region, contact.country]
                                      .filter(Boolean)
                                      .join(", ") || "Location unknown"}
                                  </p>
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="date"
                                    value={(contactEventWindows[contact.id]?.startWindow) ?? ""}
                                    onChange={(event) =>
                                      updateContactEventWindow(contact.id, "startWindow", event.target.value)
                                    }
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-1 text-xs text-foreground"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="date"
                                    value={(contactEventWindows[contact.id]?.endWindow) ?? ""}
                                    onChange={(event) =>
                                      updateContactEventWindow(contact.id, "endWindow", event.target.value)
                                    }
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-1 text-xs text-foreground"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <Button
                                    size="sm"
                                    onClick={() => void handleCreateLead(contact)}
                                    disabled={creatingId === contact.id}
                                  >
                                    {creatingId === contact.id ? "Creating…" : "Create lead"}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Enter a search term to find contacts.</p>
                  )}
                </div>
              </AccordionContent>
            </Card>
          </AccordionItem>
        </Accordion>
        )}

      <Card className="aa-glass-card w-full">
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-sm font-semibold">Pipeline</CardTitle>
            <p className="text-xs text-muted-foreground">
              {summary.total} total · {summary.confirmed} confirmed · {summary.pending} pending
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as "table" | "kanban")}>
              <TabsList>
                <TabsTrigger value="table">Table</TabsTrigger>
                <TabsTrigger value="kanban">Kanban</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={stageSelectValue} onValueChange={handleStageFilterChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STAGES_SELECT_VALUE}>All stages</SelectItem>
                {stageOptions.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {stage.charAt(0).toUpperCase() + stage.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDismiss}
              disabled={!selectedLeadIds.length || bulkDismissing}
            >
              {bulkDismissing ? "Dismissing…" : "Dismiss"}
            </Button>
            <Button size="sm" disabled={!selectedLeadIds.length || selectedLeadIds.length > 25} onClick={() => setOutreachSelection(crmLeads.filter(lead => selectedLeadIds.includes(lead.id)))}>
              Prepare outreach ({selectedLeadIds.length})
            </Button>
            {selectedLeadIds.length > 25 && <span className="text-xs">Select up to 25 venues at a time.</span>}
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleSelectAllLeads}
            >
              {allLeadsSelected ? "Deselect all" : "Select all"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={viewMode}>
            <TabsContent value="table">
              <div className="space-y-3 md:hidden">
                {filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className={`rounded-2xl border border-border bg-background/40 p-4 text-sm text-foreground/80 ${
                      highlightedLeadId === lead.id ? "border-primary/60 bg-primary/10" : ""
                    }`}
                    onClick={() => navigate(leadDetailPath(lead))}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        navigate(leadDetailPath(lead));
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <label
                        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={() => handleToggleLeadSelection(lead.id)}
                          className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                        />
                        <span className="sr-only">Select lead</span>
                      </label>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{lead.title ?? `Lead ${lead.id}`}</p>
                        <p className="text-xs text-muted-foreground">{lead.source ?? "Booking"}</p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {lead.region ?? "—"} · {deriveLeadStage(lead)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Window: {lead.event_window?.start ?? "—"} → {lead.event_window?.end ?? "—"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Updated: {new Date(lead.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {!filteredLeads.length && (
                  <p className="text-sm text-muted-foreground">No leads match the current filters.</p>
                )}
              </div>
              <div className="hidden md:block overflow-x-auto rounded-xl border border-border/60">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                          <input
                            type="checkbox"
                            checked={allLeadsSelected}
                            onChange={toggleSelectAllLeads}
                            className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                          />
                          <span className="sr-only">Select all leads</span>
                        </label>
                      </TableHead>
                      <TableHead>Venue</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Window</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow
                        key={lead.id}
                        className={`cursor-pointer hover:bg-background/40 ${
                          highlightedLeadId === lead.id ? "border border-primary/70 bg-primary/10" : ""
                        }`}
                        onClick={() => navigate(leadDetailPath(lead))}
                      >
                        <TableCell className="px-3 py-2">
                          <label
                            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedLeadIds.includes(lead.id)}
                              onChange={() => handleToggleLeadSelection(lead.id)}
                              className="h-5 w-5 rounded border border-border bg-background/70"
                            />
                            <span className="sr-only">Select lead</span>
                          </label>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-semibold text-foreground">{lead.title ?? `Lead ${lead.id}`}</span>
                            <span className="text-xs text-muted-foreground">{lead.source ?? "Booking"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{lead.region ?? "—"}</TableCell>
                        <TableCell>
                          <div
                            onClick={(event) => event.stopPropagation()}
                            role="button"
                            tabIndex={-1}
                            className="max-w-[170px]"
                          >
                            <Select
                              defaultValue={deriveLeadStage(lead)}
                              onValueChange={(value) => void handleStageChange(lead.id, value as KanbanStageId)}
                            >
                              <SelectTrigger className="w-[150px]">
                                <SelectValue placeholder="Stage" />
                              </SelectTrigger>
                              <SelectContent>
                                {stageOptions.map((stage) => (
                                  <SelectItem key={stage} value={stage}>
                                    {stage.charAt(0).toUpperCase() + stage.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <div className="flex flex-col gap-2">
                            <span>{formatLeadWindow(lead)}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                setDetailLead(lead);
                              }}
                            >
                              Edit window
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{lead.updated_at ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                    {!crmLeads.length && !loading && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10 text-center">
                          <p className="text-sm font-medium text-foreground">No booking leads yet</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Search for contacts above to create your first lead.
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
            <TabsContent value="kanban">
              <LeadsKanbanBoard
                leads={crmLeads}
                onSelectLead={(lead) => navigate(leadDetailPath(lead))}
                onMoveLead={handleMoveLead}
                canDragLead={(lead) => !bookingHasShow(lead)}
                onEditLead={handleEditLead}
                selectedLeadIds={selectedLeadIds}
                onToggleLeadSelection={(lead) => handleToggleLeadSelection(lead.id)}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {showLegacyTools && (
      <Card className="aa-glass-card w-full">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Booking Agent (Auto)</CardTitle>
          <p className="text-xs text-muted-foreground">
            Autopilot cockpit that uses the same leads for routing, outreach, and HITL approvals.
          </p>
        </CardHeader>
        <CardContent className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            <p>- Route and qualify leads automatically.</p>
            <p>- Draft outreach with HITL before sending.</p>
          </div>
          <Button asChild>
            <Link to="/agents/booking">Open Booking Agent</Link>
          </Button>
        </CardContent>
      </Card>
      )}
      </UnlockedSection>
      <LockedSection items={lockedItems} className="space-y-6" />
      {outreachSelection && <BookingOutreachDialog key={selectedArtistId} artistId={selectedArtistId} leads={outreachSelection} onClose={() => setOutreachSelection(null)} onUpdated={() => { void refresh(); }} />}
      <LeadDetailDrawer
        open={Boolean(detailLead)}
        lead={detailLead}
        canEdit
        mode="window"
        onClose={() => setDetailLead(null)}
        onSave={handleSaveWindow}
      />
      {/* Legacy drawer: mounted only when opened (owner-only manual-lead flow). */}
      {emailDrawerOpen && (
      <EmailDrawer
        open={emailDrawerOpen}
        onClose={() => {
          setEmailDrawerOpen(false);
          setEmailLead(null);
          setEmailContact(null);
          setEmailLeadIds([]);
          setEmailLeadIndex(0);
        }}
        lead={emailLead ?? undefined}
        contact={emailContact ?? undefined}
        enableAIFeatures
        sequenceControls={
          emailLeadIds.length > 1
            ? {
                index: emailLeadIndex,
                total: emailLeadIds.length,
                onPrev: emailLeadIndex > 0 ? handleSequencePrev : undefined,
                onNext: emailLeadIndex < emailLeadIds.length - 1 ? handleSequenceNext : undefined,
              }
            : undefined
        }
      />
      )}
      <BookingEventDrawer
        open={eventQueueIds.length > 0 && Boolean(currentEventInitialValues)}
        initialValues={currentEventInitialValues}
        submitting={eventSubmitting}
        onSubmit={handleSubmitEvent}
        onClose={closeEventQueueItem}
        queueIndex={eventQueueIndex}
        queueTotal={eventQueueIds.length}
        onPrev={eventQueueIndex > 0 ? handlePrevEvent : undefined}
        onNext={eventQueueIndex < eventQueueIds.length - 1 ? handleNextEvent : undefined}
        onSkip={eventQueueIds.length > 1 ? closeEventQueueItem : undefined}
      />
    </div>
  );
}

function deriveEventWindow(details?: { startWindow: string; endWindow: string }): LeadCreatePayload["event_window"] | undefined {
  if (!details) return undefined;
  const start = details.startWindow?.trim();
  const end = details.endWindow?.trim();
  if (!start && !end) return undefined;
  if (start && end) return { start, end };
  const single = start || end;
  return single ? { start: single, end: single } : undefined;
}

function formatLeadWindow(lead: CrmLead): string {
  const start = lead.event_window?.start ?? "";
  const end = lead.event_window?.end ?? "";
  if (!start && !end) return "—";
  const startLabel = start ? new Date(start).toLocaleDateString() : "TBD";
  const endLabel = end ? new Date(end).toLocaleDateString() : "TBD";
  return `${startLabel} → ${endLabel}`;
}

function renderEmailBadge(summary?: LeadEmailSummary) {
  if (!summary) {
    return (
      <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        No emails
      </span>
    );
  }
  const lastLabel = summary.last_email_at
    ? `Last ${formatRelative(summary.last_email_at)}`
    : "No recent email";
  const baseClasses =
    "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide";
  if (summary.unreplied_count > 0) {
    return (
      <span className={`${baseClasses} border-rose-500/70 bg-rose-500/10 text-rose-100`} title={lastLabel}>
        Unreplied · {summary.unreplied_count}
      </span>
    );
  }
  if (summary.has_open_thread) {
    return (
      <span className={`${baseClasses} border-sky-500/60 bg-sky-500/10 text-sky-100`} title={lastLabel}>
        Active thread
      </span>
    );
  }
  return (
    <span className={`${baseClasses} border-border text-foreground/70`} title={lastLabel}>
      {lastLabel}
    </span>
  );
}

function formatRelative(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, "minute");
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return formatter.format(diffHours, "hour");
  }
  const diffDays = Math.round(diffHours / 24);
  return formatter.format(diffDays, "day");
}
